import moment from 'moment';
import type { JsonValue, JsonObject } from 'type-fest';

import { prepareAndMakeRequest } from '@frontend/api';

import { keysToCamelCase } from 'utils/stringUtils';

// type EmailTemplate = {|
//   id: Id,
//   title: string,
//   template: string,
//   templateType: 'order_shipment' | 'quote_send',
//   customSubject: string,
//   updated: Moment,
//   updatedBy: ?string,
// |};

export const transformApiToPaperlessTerms = (data: JsonValue[]) => {
  const results = data && data.map((terms) => keysToCamelCase(terms));
  return results;
};

export const transformApiToEmailTemplate = <
  T extends JsonObject & { updated?: moment.Moment },
>(
  data: JsonObject & { updated: string },
): T & { updated: moment.Moment } => {
  return {
    ...keysToCamelCase(data),
    updated: moment(data.updated),
  } as unknown as T & { updated: moment.Moment };
};

export const registrationDetails = (key: string) =>
  prepareAndMakeRequest.get(`/registration/invite/${key}`);

export const chatRegistrationDetails = (key: string) =>
  prepareAndMakeRequest.get(`/registration/chat_invite/${key}`);

export const setPasswordDetails = (key: string) =>
  prepareAndMakeRequest.get(`/set_password/${key}`);

export default {
  general: {
    leadSourceConfig: (leadSource: string) =>
      prepareAndMakeRequest.get(`/lead_source_config/${leadSource}`),

    paperlessTerms: () => prepareAndMakeRequest.get('/user_agreements/'),

    processClasses: () => prepareAndMakeRequest.get('/process_classes/'),
  },

  auth: {
    loginWithKey: (key: string) =>
      prepareAndMakeRequest.get('/login', { query: { key } }),

    register: (key: string, data: JsonObject) =>
      prepareAndMakeRequest.post(`/registration/invite/${key}`, { body: data }),

    chatRegister: (key: string, data: JsonObject) =>
      prepareAndMakeRequest.post(`/registration/chat_invite/${key}`, {
        body: data,
      }),

    verifyEmail: (key: string) =>
      prepareAndMakeRequest.get(`/registration/verify/${key}`),
  },

  groups: {
    updateDefaultExternalShare: (data: JsonObject) => {
      return prepareAndMakeRequest.post(`/users/change_default_share_group/`, {
        body: data,
      });
    },

    updateUserGroup: (groupId: string, data: JsonObject) =>
      prepareAndMakeRequest.patch(`/users/groups/${groupId}`, { body: data }),
  },

  supplier: {
    getEmailTemplates: async (groupId: string) => {
      const response = await prepareAndMakeRequest.get<{
        results: { updated: string }[];
      }>(`/suppliers/${groupId}/emails`);
      response.results.forEach(
        (result) =>
          ((result.updated as unknown as moment.Moment) = moment(
            result.updated,
          )),
      );
      return response.results as unknown as (JsonObject & {
        updated: moment.Moment;
      })[];
    },

    createEmailTemplate: async (groupId: string, data: JsonObject) => {
      const response = await prepareAndMakeRequest.post<{ updated: string }>(
        `/suppliers/${groupId}/emails`,
        { body: data },
      );
      (response.updated as unknown as moment.Moment) = moment(response.updated);
      return response as unknown as JsonObject & { updated: moment.Moment };
    },

    updateEmailTemplate: async (templateId: string, data: JsonObject) => {
      const response = await prepareAndMakeRequest.put<{ updated: string }>(
        `/suppliers/emails/${templateId}`,
        { body: data },
      );
      (response.updated as unknown as moment.Moment) = moment(response.updated);
      return response as unknown as JsonObject & { updated: moment.Moment };
    },

    deleteEmailTemplate: (templateId: string) =>
      prepareAndMakeRequest.delete(`/suppliers/emails/${templateId}`),

    getCustomerFacingNotes: (groupId: string) =>
      prepareAndMakeRequest.get(`/suppliers/${groupId}/quote_notes`),

    getTermsAndConditions: (groupId: string) =>
      prepareAndMakeRequest.get(`/suppliers/${groupId}/terms`),

    getManufacturerNotes: (groupId: string) =>
      prepareAndMakeRequest.get(`/suppliers/${groupId}/manufacturer_notes`),

    getConformanceLanguage: (groupId: string) =>
      prepareAndMakeRequest.get(`/suppliers/${groupId}/coc`),

    getShopVariables: (groupId: string) =>
      prepareAndMakeRequest.get(`/suppliers/${groupId}/shop_variables`),

    getUserDefinedKeys: () =>
      prepareAndMakeRequest.get('/quotes/metadata_keys'),

    getCustomPartAttrs: async (groupId: string) =>
      prepareAndMakeRequest.get(`/suppliers/${groupId}/custom_part_attrs`),

    // TODO: We should remove this endpoint
    update: (groupId: string, data: JsonObject) =>
      prepareAndMakeRequest.patch(`/suppliers/${groupId}`, { body: data }),

    updateConformanceLanguage: async (groupId: string, data: JsonObject) =>
      prepareAndMakeRequest.post(`/suppliers/${groupId}/coc`, { body: data }),

    /**
     * POST's a request to the backend, updating shop markup and overhead information.
     * @param {*} groupId the id of the group whose markup and overhead will be updated
     * @param {*} data    an object containing a "markup" field and an "overhead" field,
     *                    both of which are floats
     */
    updateShopVariables: (groupId: string, data: JsonObject) =>
      prepareAndMakeRequest.patch(`/suppliers/${groupId}/shop_variables`, {
        body: data,
      }),

    updateCustomPartAttrs: (groupId: string, data: JsonObject) =>
      prepareAndMakeRequest.patch(`/suppliers/${groupId}/custom_part_attrs`, {
        body: data,
      }),

    updateSupplierInternalNotes: (groupId: string, data: JsonObject) =>
      prepareAndMakeRequest.patch(`/suppliers/${groupId}/internal_notes`, {
        body: data,
      }),

    updateSupplierCustomerFacingNotes: (groupId: string, data: JsonObject) =>
      prepareAndMakeRequest.post(`/suppliers/${groupId}/quote_notes`, {
        body: data,
      }),

    updateSupplierTermsAndConditions: async (
      groupId: string,
      data: JsonObject,
    ) =>
      prepareAndMakeRequest.post(`/suppliers/${groupId}/terms`, { body: data }),

    updateManufacturerNotes: async (groupId: string, data: JsonObject) =>
      prepareAndMakeRequest.put(`/suppliers/${groupId}/manufacturer_notes`, {
        body: data,
      }),
  },

  finishes: {
    list: () => prepareAndMakeRequest.get('/finishes'),
  },

  payments: {
    patchConnectedAccount: (groupId: string, data: JsonObject) =>
      prepareAndMakeRequest.patch(`/payments/${groupId}/stripe_account`, {
        body: data,
      }),
  },

  integrations: {
    getQuickbooksInfo: (groupId: string) =>
      prepareAndMakeRequest.get(`/integrations/quickbooks/groups/${groupId}`),

    getAPIToken: () => prepareAndMakeRequest.get('/integrations/api_token'),

    postAPIToken: () => prepareAndMakeRequest.post('/integrations/api_token'),

    deleteAPIToken: () =>
      prepareAndMakeRequest.delete('/integrations/api_token'),
  },
};
